import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Package, ExternalLink, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const categories = [
  {
    key: 'partners',
    Icon: Handshake,
    tone: '#FF8C00',
    title: { fr: 'Partenaires', en: 'Partners' },
    intro: {
      fr: "Nous collaborons avec des partenaires stratégiques qui renforcent notre capacité à livrer des projets de qualité, dans les délais et selon les standards internationaux.",
      en: 'We collaborate with strategic partners who strengthen our capacity to deliver quality projects, on time and to international standards.',
    },
    partners: [
      {
        name: 'Lior Technologie & Ingénierie',
        logo: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/a607bdba6afda6a2_LTI.png',
        detailPath: '/qui-sommes-nous/nos-partenaires/lior-technologie-ingenierie',
      },
    ],
  },
  {
    key: 'suppliers',
    Icon: Package,
    tone: '#702963',
    title: { fr: 'Fournisseurs & matériaux', en: 'Suppliers & materials' },
    intro: {
      fr: "Ciment, aciers, briques cuites, agrégats, menuiseries : nous travaillons avec des fournisseurs contrôlés pour garantir qualité, traçabilité et respect des délais.",
      en: 'Cement, steel, fired bricks, aggregates, joinery: we work with controlled suppliers to guarantee quality, traceability and on-time delivery.',
    },
    partners: [
      {
        name: 'CIMKO',
        url: 'https://cimko.cd/',
        bg: '#22a2d1',
        accent: '#c10e0e',
      },
      {
        name: 'Leroy Merlin',
        url: 'https://www.leroymerlin.fr/',
        bg: '#78BE20',
        accent: '#5a9013',
      },
      {
        name: 'BayWare',
        url: 'https://solarshop.baywa-re.fr/search',
        bg: '#00A651',
        accent: '#007a3c',
      },
    ],
  },
];

const NosPartenairesPage = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[240px]" data-testid="partners-page">
        {/* Hero */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/w1q36yir_IMG_6320.jpg')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <span className="inline-block text-orange-400 font-semibold tracking-widest text-sm mb-3">
              {t(language, 'GCI · RÉSEAU', 'GCI · NETWORK')}
            </span>
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
              {t(language, 'Nos partenaires', 'Our partners')}
            </h1>
            <div className="mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(
                language,
                "Un projet de construction réussi repose sur un écosystème d'acteurs de confiance. Chez GCI, nous nous entourons de partenaires locaux et internationaux sélectionnés pour leur savoir-faire, leur fiabilité et leur engagement qualité.",
                'A successful construction project relies on an ecosystem of trusted actors. At GCI, we surround ourselves with local and international partners selected for their expertise, reliability and quality commitment.'
              )}
            </p>
          </div>
        </section>

        {categories.map((cat) => {
          const Icon = cat.Icon;
          return (
            <section key={cat.key} className="py-14 md:py-20" data-testid={`category-${cat.key}`}>
              <div className="max-w-6xl mx-auto px-6">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-10">
                  <div
                    className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full text-white shadow-lg"
                    style={{ backgroundColor: cat.tone }}
                  >
                    <Icon className="w-8 h-8" strokeWidth={2} />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#702963' }}>
                      {cat.title[language] || cat.title.fr}
                    </h2>
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed mt-2 max-w-3xl">
                      {cat.intro[language] || cat.intro.fr}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {cat.partners.map((p) =>
                    p.detailPath ? (
                      <Link
                        key={p.name}
                        to={p.detailPath}
                        className="bg-white border border-gray-200 border-l-4 p-6 rounded-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block"
                        style={{ borderLeftColor: cat.tone }}
                        data-testid={`partner-${cat.key}-${p.name}`}
                      >
                        <img
                          src={p.logo}
                          alt={p.name}
                          className="h-28 w-auto object-contain mb-4"
                        />
                        <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold mt-2" style={{ color: cat.tone }}>
                          {language === 'fr' ? 'Découvrir le partenaire' : 'Discover the partner'}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </Link>
                    ) : p.url ? (
                      <a
                        key={p.name}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border-l-4 p-6 rounded-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
                        style={{ backgroundColor: p.bg, borderLeftColor: p.accent }}
                        data-testid={`partner-${cat.key}-${p.name}`}
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-white">{p.name}</h3>
                          <ExternalLink className="w-4 h-4 text-white/80" />
                        </div>
                        <p className="text-xs text-white/85 mt-2 break-all">
                          {p.url.replace('https://', '').replace(/\/$/, '')}
                        </p>
                      </a>
                    ) : (
                      <div
                        key={p.name}
                        className="bg-white border-l-4 border border-gray-200 p-6 rounded-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                        style={{ borderLeftColor: cat.tone }}
                        data-testid={`partner-${cat.key}-${p.name}`}
                      >
                        <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                        {p.city && (
                          <p className="text-xs text-gray-500 mt-1 uppercase tracking-widest">
                            {p.city}
                          </p>
                        )}
                        {p.desc && (
                          <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                            {p.desc[language] || p.desc.fr}
                          </p>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </section>
          );
        })}

        {/* Disclaimer */}
        <section className="py-10 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center text-sm text-gray-500 italic">
            {t(
              language,
              "Liste indicative de partenaires. Notre réseau évolue au rythme des projets et des besoins de nos clients.",
              'Indicative list of partners. Our network evolves with our clients\u2019 projects and needs.'
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NosPartenairesPage;
